import { DS_HEIGHT } from '@calm/evexia-design-tokens';
import {
    Box,
    Fade,
    Grid,
    GridProps,
    Stack,
    SxProps,
    Theme,
    Typography,
    TypographyProps,
} from '@rhg/libs/material';
import { sxAppend } from '@rhg/libs/material/sx';
import { ElementType, PropsWithChildren, ReactNode } from 'react';
import { StreamingTypography } from '../streaming-typography';
import { TextWithLinks } from '../text-with-links';

const styleSubtitle = {
    whiteSpace: 'pre-line',
};

const styleInlineComponentGrid = {
    display: 'flex',
    flexGrow: 1,
};

export const SubtitlePosition = {
    Below: 'below',
    Inline: 'inline',
} as const;

type SubtitlePosition =
    (typeof SubtitlePosition)[keyof typeof SubtitlePosition];

interface SubtitleComponentProps {
    subtitle: string | ReactNode;
    subtitleComponent: ElementType;
    subtitleVariant: TypographyProps['variant'];
    subtitleColor: TypographyProps['color'];
    subtitleAlign: TypographyProps['textAlign'];
    subtitleStyle?: SxProps<Theme>;
    subtitleWillRenderLinks: boolean;
}

interface ConditionalFadeProps {
    in: boolean;
    timeout?: number;
}

function ConditionalFade({
    in: fadeIn,
    timeout,
    children,
}: PropsWithChildren<ConditionalFadeProps>) {
    if (timeout && timeout > 0) {
        return (
            <Fade in={fadeIn} timeout={timeout}>
                <Box>{children}</Box>
            </Fade>
        );
    }
    return <>{children}</>;
}

function SubtitleComponent({
    subtitle,
    subtitleComponent,
    subtitleVariant,
    subtitleColor,
    subtitleAlign,
    subtitleStyle,
    subtitleWillRenderLinks,
}: SubtitleComponentProps) {
    return (
        <Typography
            component={subtitleComponent}
            variant={subtitleVariant}
            color={subtitleColor}
            textAlign={subtitleAlign}
            sx={sxAppend(styleSubtitle, subtitleStyle)}
        >
            {subtitleWillRenderLinks ? (
                <TextWithLinks text={subtitle} />
            ) : (
                subtitle
            )}
        </Typography>
    );
}

/**
 * Conditionally wraps children with a horizontal Stack if a titleButton is provided
 * This ensures backwards compatibility for the styling of existing uses of Heading without titleButton
 */
function TitleAndButtonContainer({
    titleButton,
    sx,
    children,
}: PropsWithChildren<{ titleButton?: ReactNode; sx?: SxProps<Theme> }>) {
    if (!titleButton) {
        return children;
    }

    return (
        <Stack sx={sx} direction="row" alignItems="center" gap={2}>
            {children}
            {titleButton}
        </Stack>
    );
}

/**
 * `shouldUseStreamingHeadingEffect` and `title` are correlated rather than
 * independent booleans/values: StreamingTypography animates word-by-word,
 * which only makes sense for plain text, so a rich (non-string) title can
 * never be paired with the streaming effect. Modeled as a discriminated
 * union (rather than `title: ReactNode` + `shouldUseStreamingHeadingEffect:
 * boolean` as two separate fields) so passing both together is a compile
 * error instead of relying on an internal `as string` cast.
 */
type TitleContentProps =
    | { title?: ReactNode; shouldUseStreamingHeadingEffect?: false }
    | { title: string; shouldUseStreamingHeadingEffect: true };

type TitleComponentProps = TitleContentProps & {
    titleComponent: ElementType;
    titleVariant: TypographyProps['variant'];
    titleColor: TypographyProps['color'];
    titleAlign: TypographyProps['textAlign'];
    titleStyle?: SxProps<Theme>;
    titleAndButtonContainerStyle?: SxProps<Theme>;
    titleId?: string;
    titleAriaLabel?: string;
    titleButton?: ReactNode;
    disableStreamingAnimation?: boolean;
    onStreamingComplete?: () => void;
};

function TitleComponent(props: TitleComponentProps) {
    const {
        titleComponent,
        titleVariant,
        titleColor,
        titleAlign,
        titleStyle,
        titleAndButtonContainerStyle,
        titleId,
        titleAriaLabel,
        titleButton,
        disableStreamingAnimation = false,
        onStreamingComplete,
    } = props;

    return (
        <TitleAndButtonContainer
            titleButton={titleButton}
            sx={titleAndButtonContainerStyle}
        >
            {props.shouldUseStreamingHeadingEffect ? (
                <StreamingTypography
                    component={titleComponent}
                    variant={titleVariant}
                    color={titleColor}
                    textAlign={titleAlign}
                    sx={titleStyle}
                    onComplete={onStreamingComplete}
                    id={titleId}
                    aria-label={titleAriaLabel}
                    disableAnimation={disableStreamingAnimation}
                >
                    {props.title}
                </StreamingTypography>
            ) : (
                <Typography
                    component={titleComponent}
                    variant={titleVariant}
                    color={titleColor}
                    sx={titleStyle}
                    textAlign={titleAlign}
                    id={titleId}
                    aria-label={titleAriaLabel}
                >
                    {props.title}
                </Typography>
            )}
        </TitleAndButtonContainer>
    );
}

interface HeadingPropsBase {
    /** Test ID for the heading container element */
    testId?: string;
    /** Typography variant for the title (e.g., 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'). Defaults to 'h4' */
    titleVariant?: TypographyProps['variant'];
    /** Color of the title text. Defaults to 'text.primary' */
    titleColor?: TypographyProps['color'];
    /** Semantic HTML element for the title (e.g., 'h1', 'h2', 'h3', etc.). Defaults to 'h1' */
    titleComponent?: ElementType;
    /** Text alignment for the title. Defaults to 'left' */
    titleAlign?: TypographyProps['textAlign'];
    /** Custom styles for the title Typography component */
    titleStyle?: SxProps<Theme>;
    /** Custom styles for the container that wraps the title and titleButton */
    titleAndButtonContainerStyle?: SxProps<Theme>;
    /** Grid column breakpoint sizes for the title container */
    titleColumns?: Pick<GridProps, 'xl' | 'lg' | 'md' | 'sm' | 'xs'>;
    /** HTML id attribute for the title element */
    titleId?: string;
    /** Aria label for the title element for accessibility */
    titleAriaLabel?: string;
    /** Button or element to render inline with the title */
    titleButton?: ReactNode;
    /** The subtitle text content (can be a string or React node) */
    subtitle?: string | ReactNode;
    /** Typography variant for the subtitle. Defaults to 'subtitle1' */
    subtitleVariant?: TypographyProps['variant'];
    /** Color of the subtitle text. Defaults to 'text.sub' */
    subtitleColor?: TypographyProps['color'];
    /** Semantic HTML element for the subtitle. Defaults to 'p' */
    subtitleComponent?: ElementType;
    /** Text alignment for the subtitle. Defaults to 'left' */
    subtitleAlign?: TypographyProps['textAlign'];
    /** Custom styles for the subtitle Typography component */
    subtitleStyle?: SxProps<Theme>;
    /** If true, subtitle will be processed to render clickable links */
    subtitleWillRenderLinks?: boolean;
    /** Grid column breakpoint sizes for the subtitle container */
    subTitleColumns?: Pick<GridProps, 'xl' | 'lg' | 'md' | 'sm' | 'xs'>;
    /** Spacing between title and subtitle (in theme spacing units). Defaults to 2 */
    spacing?: number;
    /**
     * If `true`, the subtitle will render above the title.
     */
    reversed?: boolean;
    /**
     * Controls where the subtitle is positioned:
     * - 'below': Default behavior - subtitle spans full width below title/buttons
     * - 'inline': Subtitle appears inline with the title, not spanning under buttons
     */
    subtitlePosition?: SubtitlePosition;
    /** Renders to the left of the heading */
    leftButton?: ReactNode;
    /** Renders to the right of the heading */
    rightButton?: ReactNode;
    /** Custom styles for the root Grid container */
    sx?: SxProps<Theme>;
    /** Data-testid attribute for testing */
    ['data-testid']?: string;
    /** If true, skip streaming animation and show all text immediately (useful for visual testing) */
    disableStreamingAnimation?: boolean;
    /* Function to take additional action once streaming text animation completes */
    onStreamingComplete?: () => void;
    /* Timeout duration for subtitle fade-in effect */
    subtitleFadeTimeout?: number;
    /*
     * Determines whether the subtitle should fade in
     * Used in cases where we want to conditionally display the subtitle after the typewriter animation runs
     */
    subtitleFadeIn?: boolean;
}

/**
 * `title` is almost always a string, but accepts any ReactNode for rich
 * content (e.g. an inline logo swapped in for a word via react-intl
 * rich-text formatting - see `titleAriaLabel` for providing the plain-text
 * equivalent screen readers should announce instead). See TitleContentProps'
 * own docs for why that's not supported together with
 * `shouldUseStreamingHeadingEffect`, and why the two are one combined type
 * rather than two separate, independently-typed props.
 */
type HeadingProps = HeadingPropsBase & TitleContentProps;

const stylePlaceholder = {
    height: DS_HEIGHT.BUTTON_LARGE,
    minWidth: DS_HEIGHT.BUTTON_LARGE,
};

export function Placeholder({ sx }: { sx?: SxProps<Theme> }) {
    return <Box sx={sxAppend(stylePlaceholder, sx)} />;
}

export function Heading({
    testId,
    titleVariant = 'h4',
    titleColor = 'text.primary',
    titleComponent = 'h1',
    titleAlign = 'left',
    titleStyle,
    titleAndButtonContainerStyle,
    titleColumns,
    titleId,
    titleAriaLabel,
    titleButton,
    subtitle,
    subtitleVariant = 'subtitle1',
    subtitleColor = 'text.sub',
    subtitleComponent = 'p',
    subtitleAlign = 'left',
    subtitleStyle,
    subtitleWillRenderLinks = false,
    subTitleColumns,
    spacing = 2,
    reversed = false,
    subtitlePosition = SubtitlePosition.Below,
    rightButton,
    leftButton,
    sx,
    disableStreamingAnimation = false,
    onStreamingComplete,
    subtitleFadeTimeout,
    subtitleFadeIn = false,
    // `title`/`shouldUseStreamingHeadingEffect` stay bundled together here
    // (see TitleContentProps' own docs) rather than being destructured into
    // two independent bindings - that would lose the correlation TypeScript
    // needs to enforce a rich `title` can't be paired with the streaming
    // effect, forcing an unchecked cast back in at the TitleComponent call
    // below instead.
    ...titleContent
}: HeadingProps) {
    return (
        <Grid
            {...(testId ? { 'data-testid': testId } : {})}
            container
            sx={sx}
            direction="row"
            flexWrap="nowrap"
            alignItems="center"
        >
            <Grid
                container
                direction={reversed ? 'column-reverse' : 'column'}
                alignItems={titleAlign === 'center' ? 'center' : 'stretch'}
                justifyContent="center"
                gap={spacing}
                flexWrap="nowrap"
            >
                {(titleContent.title || leftButton || rightButton) && (
                    <Stack
                        direction="row"
                        width="100%"
                        gap={6}
                        justifyContent="space-between"
                    >
                        {leftButton ? (
                            <Grid item flexShrink={0}>
                                {leftButton}
                            </Grid>
                        ) : rightButton && titleAlign === 'center' ? (
                            <Placeholder />
                        ) : null}
                        {titleContent.title && (
                            <Grid
                                container
                                justifyContent={
                                    titleAlign !== 'center'
                                        ? 'flex-start'
                                        : 'center'
                                }
                                flexGrow={1}
                                alignItems="center"
                            >
                                <Grid
                                    item
                                    {...titleColumns}
                                    sx={styleInlineComponentGrid}
                                    direction={
                                        reversed ? 'column-reverse' : 'column'
                                    }
                                >
                                    <TitleComponent
                                        {...titleContent}
                                        titleComponent={titleComponent}
                                        titleVariant={titleVariant}
                                        titleColor={titleColor}
                                        titleAlign={titleAlign}
                                        titleStyle={titleStyle}
                                        titleAndButtonContainerStyle={
                                            titleAndButtonContainerStyle
                                        }
                                        titleId={titleId}
                                        titleAriaLabel={titleAriaLabel}
                                        titleButton={titleButton}
                                        disableStreamingAnimation={
                                            disableStreamingAnimation
                                        }
                                        onStreamingComplete={
                                            onStreamingComplete
                                        }
                                    />
                                    {subtitle &&
                                        subtitlePosition ===
                                            SubtitlePosition.Inline && (
                                            <SubtitleComponent
                                                subtitle={subtitle}
                                                subtitleComponent={
                                                    subtitleComponent
                                                }
                                                subtitleVariant={
                                                    subtitleVariant
                                                }
                                                subtitleColor={subtitleColor}
                                                subtitleAlign={subtitleAlign}
                                                subtitleStyle={subtitleStyle}
                                                subtitleWillRenderLinks={
                                                    subtitleWillRenderLinks
                                                }
                                            />
                                        )}
                                </Grid>
                            </Grid>
                        )}
                        {rightButton ? (
                            <Grid item flexShrink={0}>
                                {rightButton}
                            </Grid>
                        ) : leftButton && titleAlign === 'center' ? (
                            <Placeholder />
                        ) : null}
                    </Stack>
                )}
                {subtitle && subtitlePosition === SubtitlePosition.Below && (
                    <ConditionalFade
                        in={subtitleFadeIn}
                        timeout={subtitleFadeTimeout}
                    >
                        <Grid
                            container
                            justifyContent={
                                subtitleAlign !== 'center'
                                    ? 'flex-start'
                                    : 'center'
                            }
                        >
                            {/* Full-width subtitle for non-centered layouts; centered layouts use auto-width or subTitleColumns */}
                            <Grid
                                item
                                {...(titleAlign !== 'center' && { xs: 12 })}
                                {...subTitleColumns}
                            >
                                <SubtitleComponent
                                    subtitle={subtitle}
                                    subtitleComponent={subtitleComponent}
                                    subtitleVariant={subtitleVariant}
                                    subtitleColor={subtitleColor}
                                    subtitleAlign={subtitleAlign}
                                    subtitleStyle={subtitleStyle}
                                    subtitleWillRenderLinks={
                                        subtitleWillRenderLinks
                                    }
                                />
                            </Grid>
                        </Grid>
                    </ConditionalFade>
                )}
            </Grid>
        </Grid>
    );
}
